import React from 'react';
import {
  Symbol,
  Link,
  ICON_CLOSE,
  ICON_MENU,
  SymbolDirection,
} from '@doveit/lego';
import { URLObject } from '../../utils/links';
import * as styles from './MainNavigation.style';
import { ARROW_TOP } from '../../themes/icons';

export interface NavigationItem {
  id: string,
  urlObject: URLObject,
  label: string,
  onClick: VoidFunction,
  active?: boolean,
  subItems?: NavigationItem[],
}

export interface MainNavigationProps {
  render: {
    logo?: React.ReactElement,
    actions?: React.ReactElement,
  },
  items?: NavigationItem[],
  navigationControlHandler?: VoidFunction,
  mobileMenuOpen?: boolean,
}

const MainNavigation: React.FC<MainNavigationProps> = ({
  render = {},
  items = [],
  navigationControlHandler,
  mobileMenuOpen,
}) => {
  const [currentSubMenuOpen, setCurrentSubMenuOpen] = React.useState<string>();

  const onItemHoverHandler = React.useCallback(
    (id: string) => () => setCurrentSubMenuOpen(id), [],
  );

  const onItemLeaveHandler = () => setCurrentSubMenuOpen(undefined);

  return (
    <nav role="navigation">
      <div css={styles.wrapper}>
        <div css={styles.sideWrapper}>
          {(navigationControlHandler && typeof navigationControlHandler === 'function') && (
            <button
              css={styles.control}
              onClick={navigationControlHandler}
              data-ref="main-navigation-control"
              aria-label="Controllo navigazione"
            >
              <Symbol path={mobileMenuOpen ? ICON_CLOSE : ICON_MENU} />
            </button>
          )}
          <div css={styles.logo}>
            {render.logo}
          </div>
        </div>
        {(items && items.length > 0) && (
          <ul
            data-ref="main-navigation-items"
            css={styles.items}
          >
            {items.map((item) => (
              <li
                key={item.id}
                css={styles.item}
                data-ref="main-navigation-item"
                aria-label="First level navigation item"
                onMouseEnter={onItemHoverHandler(item.id)}
                onMouseLeave={onItemLeaveHandler}
                $active={item.active}
              >
                <Link
                  css={styles.link}
                  href={item.urlObject.as}
                  onClick={item.onClick}
                  data-ref="main-navigation-link"
                  aria-label="First level navigation link"
                >
                  {item.label}
                </Link>
                {item.subItems && item.subItems.length > 0 && (
                  <div
                    css={styles.secondLevelMenu}
                    $open={currentSubMenuOpen === item.id}
                  >
                    <ul
                      css={styles.secondLevelMenuList}
                      $subItemsCount={item.subItems.length}
                    >
                      {item.subItems.map((secondLevelItem) => (
                        <li
                          key={secondLevelItem.id}
                          aria-label="Second level navigation item"
                        >
                          <Link
                            css={styles.secondLevelMenuItem}
                            href={secondLevelItem.urlObject.as}
                            onClick={secondLevelItem.onClick}
                            aria-label="Second level navigation link"
                          >
                            {secondLevelItem.label}
                          </Link>
                          {secondLevelItem.subItems && secondLevelItem.subItems.length > 0 && (
                            <ul>
                              {secondLevelItem.subItems.map((thirdLevelItem) => (
                                <li
                                  key={thirdLevelItem.id}
                                  aria-label="Third level navigation item"
                                >
                                  <Link
                                    css={styles.thirdLevelMenuItem}
                                    href={thirdLevelItem.urlObject.as}
                                    onClick={thirdLevelItem.onClick}
                                    aria-label="Third level navigation link"
                                  >
                                    {thirdLevelItem.label}
                                  </Link>
                                  {thirdLevelItem.subItems && thirdLevelItem.subItems.length > 0 && (
                                    <div>
                                      <ul>
                                        {thirdLevelItem.subItems.map((fourthLevelItem) => (
                                          <li
                                            key={fourthLevelItem.id}
                                            aria-label="Fourth level navigation item"
                                          >
                                            <Link
                                              css={styles.fourthLevelMenuItem}
                                              href={fourthLevelItem.urlObject.as}
                                              onClick={fourthLevelItem.onClick}
                                              aria-label="Fourth level navigation link"
                                            >
                                              <Symbol
                                                path={ARROW_TOP}
                                                size={12}
                                                direction={SymbolDirection.RIGHT}
                                              />
                                              {fourthLevelItem.label}
                                            </Link>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {render.actions && (
          <div>
            {render.actions}
          </div>
        )}
      </div>
    </nav>
  );
};

export default MainNavigation;
